import { useCallback, useContext, useEffect, useRef, useState, type MouseEvent } from 'react';

import { Spinner, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ArrowDownload24Regular, ChevronLeft24Regular, ChevronRight24Regular, Dismiss24Regular, Share24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { downloadFileContent } from '../api/useResourceUrl';
import { useResourceFullSize, useVideoStreamUrl } from '../api/useResourceUrl';
import { sharableLinkDownloadUrl } from '../utils';
import { useResourceScheme, useSharableLink, ResourceSchemeContext, SharableLinkContext } from './ResourceSchemeContext';

export interface GalleryItem {
  fileId: string;
  type: 'image' | 'video';
}

interface ResourceGalleryProps {
  items: GalleryItem[];
  startIndex: number;
  domainId: number;
  onClose: () => void;
}

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.92)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000000,
    outline: 'none',
  },
  topButtons: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  actionButton: {
    color: '#fff',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: tokens.borderRadiusCircular,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    ':focus-visible': {
      outline: '2px solid #fff',
      outlineOffset: '2px',
    },
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    cursor: 'pointer',
    padding: '12px',
    borderRadius: tokens.borderRadiusCircular,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
    ':focus-visible': {
      outline: '2px solid #fff',
      outlineOffset: '2px',
    },
    ':disabled': {
      opacity: 0.3,
      cursor: 'default',
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
  },
  navLeft: {
    left: '16px',
  },
  navRight: {
    right: '16px',
  },
  media: {
    maxWidth: 'calc(100vw - 120px)',
    maxHeight: 'calc(100vh - 80px)',
    objectFit: 'contain',
  },
  counter: {
    position: 'absolute',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: tokens.fontSizeBase300,
    fontFamily: tokens.fontFamilyBase,
    userSelect: 'none',
  },
  errorText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: tokens.fontSizeBase300,
  },
  copiedTooltip: {
    position: 'absolute',
    top: '56px',
    right: '16px',
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyBase,
    padding: '4px 8px',
    borderRadius: tokens.borderRadiusMedium,
    pointerEvents: 'none' as const,
  },
});

function GalleryMedia({ domainId, item }: { domainId: number; item: GalleryItem }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [mediaLoaded, setMediaLoaded] = useState(false);

  const scheme = useResourceScheme(item.fileId);
  const isApiResource = scheme.startsWith('xm');
  const sharableLink = useSharableLink(item.fileId);

  const effectiveFileId = isApiResource ? item.fileId : undefined;
  const imageQuery = useResourceFullSize(domainId, effectiveFileId, item.type === 'image');
  const videoQuery = useVideoStreamUrl(domainId, effectiveFileId, item.type === 'video');

  // Reset loaded state when the item changes
  useEffect(() => {
    setMediaLoaded(false);
  }, [item.fileId]);

  const altText = item.type === 'image' ? t('images') : t('recordings');
  const spinner = !mediaLoaded && <Spinner appearance="inverted" label={t('loading')} />;

  if (!isApiResource && sharableLink) {
    const downloadUrl = sharableLinkDownloadUrl(sharableLink);
    if (item.type === 'image') return <>
      {spinner}
      <img className={styles.media} src={downloadUrl} alt={altText} onLoad={() => setMediaLoaded(true)} style={mediaLoaded ? undefined : { display: 'none' }} />
    </>;
    if (item.type === 'video') return <>
      {spinner}
      <video className={styles.media} src={downloadUrl} controls autoPlay key={item.fileId} onLoadedData={() => setMediaLoaded(true)} style={mediaLoaded ? undefined : { display: 'none' }} />
    </>;
  }

  const isLoading = item.type === 'image' ? imageQuery.isLoading : videoQuery.isLoading;
  const isError = item.type === 'image' ? imageQuery.isError : videoQuery.isError;

  if (isLoading) return <Spinner appearance="inverted" label={t('loading')} />;
  if (isError) return <span className={styles.errorText} role="alert">{t('failedToLoadResource')}</span>;

  if (item.type === 'image' && imageQuery.data) {
    return <>
      {spinner}
      <img className={styles.media} src={imageQuery.data} alt={altText} onLoad={() => setMediaLoaded(true)} style={mediaLoaded ? undefined : { display: 'none' }} />
    </>;
  }

  if (item.type === 'video' && videoQuery.data) {
    return <>
      {spinner}
      <video className={styles.media} src={videoQuery.data} controls autoPlay key={item.fileId} onLoadedData={() => setMediaLoaded(true)} style={mediaLoaded ? undefined : { display: 'none' }} />
    </>;
  }

  return null;
}

export default function ResourceGallery({ items, startIndex, domainId, onClose }: ResourceGalleryProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const queryClient = useQueryClient();
  const schemeMap = useContext(ResourceSchemeContext);
  const sharableLinkMap = useContext(SharableLinkContext);

  const [currentIndex, setCurrentIndex] = useState(
    Math.max(0, Math.min(startIndex, items.length - 1)),
  );
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const showNav = items.length > 1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < items.length - 1;

  const goToPrev = useCallback(() => {
    setCurrentIndex(i => Math.max(0, i - 1));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex(i => Math.min(items.length - 1, i + 1));
  }, [items.length]);

  // Save trigger element and focus overlay on mount; restore focus on unmount
  useEffect(() => {
    triggerRef.current = document.activeElement;
    overlayRef.current?.focus();
    return () => {
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, []);

  // Focus trap
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    function handleFocusTrap(e: FocusEvent) {
      if (overlay && e.relatedTarget instanceof Node && !overlay.contains(e.relatedTarget)) {
        overlay.focus();
      }
    }

    overlay.addEventListener('focusout', handleFocusTrap);
    return () => overlay.removeEventListener('focusout', handleFocusTrap);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case 'ArrowLeft':
          goToPrev();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Escape':
          onCloseRef.current();
          break;
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goToPrev, goToNext]);

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Prefetch adjacent images (only for API resources)
  useEffect(() => {
    const prefetch = (index: number) => {
      const item = items[index];
      if (!item || item.type !== 'image') return;
      const itemScheme = schemeMap[item.fileId] ?? 'xm';
      if (!itemScheme.startsWith('xm')) return;
      queryClient.prefetchQuery({
        queryKey: ['resourceFullSize', domainId, item.fileId],
        queryFn: async () => {
          const blob = await downloadFileContent(domainId, item.fileId);
          return URL.createObjectURL(blob);
        },
        staleTime: Infinity,
      });
    };
    prefetch(currentIndex - 1);
    prefetch(currentIndex + 1);
  }, [currentIndex, items, domainId, queryClient, schemeMap]);

  const handleDownload = useCallback(async () => {
    const item = items[currentIndex];
    if (!item) return;
    const itemScheme = schemeMap[item.fileId] ?? 'xm';
    const itemSharableLink = sharableLinkMap[item.fileId];
    if (!itemScheme.startsWith('xm') && itemSharableLink) {
      window.open(sharableLinkDownloadUrl(itemSharableLink), '_blank');
      return;
    }
    const blob = await downloadFileContent(domainId, item.fileId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = item.fileId;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [currentIndex, items, domainId, schemeMap, sharableLinkMap]);

  if (items.length === 0) return null;

  function handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  return (
    <div
      ref={overlayRef}
      tabIndex={-1}
      className={styles.overlay}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={t('resources')}
    >
      <div className={styles.topButtons}>
        {sharableLinkMap[items[currentIndex].fileId] && (
          <button
            className={styles.actionButton}
            onClick={() => {
              const link = sharableLinkMap[items[currentIndex].fileId];
              if (link) {
                navigator.clipboard.writeText(link);
                setCopied(true);
                clearTimeout(copiedTimerRef.current);
                copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
              }
            }}
            aria-label={t('share')}
          >
            <Share24Regular />
          </button>
        )}
        <button className={styles.actionButton} onClick={handleDownload} aria-label={t('download')}>
          <ArrowDownload24Regular />
        </button>
        <button className={styles.actionButton} onClick={onClose} aria-label={t('close')}>
          <Dismiss24Regular />
        </button>
      </div>
      <span aria-live="polite" className={copied ? styles.copiedTooltip : undefined}>
        {copied ? t('copied') : ''}
      </span>

      {showNav && (
        <button
          className={mergeClasses(styles.navButton, styles.navLeft)}
          onClick={goToPrev}
          disabled={!hasPrev}
          aria-label={t('previous')}
        >
          <ChevronLeft24Regular />
        </button>
      )}

      <GalleryMedia domainId={domainId} item={items[currentIndex]} />

      {showNav && (
        <button
          className={mergeClasses(styles.navButton, styles.navRight)}
          onClick={goToNext}
          disabled={!hasNext}
          aria-label={t('next')}
        >
          <ChevronRight24Regular />
        </button>
      )}

      {showNav && (
        <span className={styles.counter} aria-live="polite">
          {t('galleryCounter', { current: currentIndex + 1, total: items.length })}
        </span>
      )}
    </div>
  );
}
